import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'

const Report = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [userRole, setUserRole] = useState(null)
  const [stats, setStats] = useState({
    totalMembers: 0,
    freeMembers: 0,
    vipMembers: 0,
    lifetimeMembers: 0,
    nearExpiryMembers: 0,
    expiredMembers: 0,
    todayRevenue: 0,
    yesterdayRevenue: 0,
    todayTransactions: 0,
    monthlyRevenue: 0,
    monthlyExpenses: 0,
    monthlyProfit: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    totalProfit: 0,
    operatingTime: '',  // เพิ่ม state สำหรับเก็บระยะเวลาทำการ
    periodStats: []
  })

  const [periodType, setPeriodType] = useState('daily')

  // เพิ่ม state สำหรับฟอร์มบันทึกรายจ่าย
  const [expenseForm, setExpenseForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: ''
  })

  // เพิ่ม state สำหรับเก็บรายการรายจ่าย
  const [expenses, setExpenses] = useState([])

  // ตรวจสอบสิทธิ์ admin
  useEffect(() => {
    const checkAdminRole = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        console.log('No session found')
        navigate('/')
        return
      }

      try {
        console.log('Checking admin role for user:', session.user.id)
        const { data, error } = await supabase
          .from('profiles')
          .select('membership')
          .eq('id', session.user.id)
          .single()

        if (error) {
          console.error('Error fetching profile:', error)
          throw error
        }

        console.log('Profile data:', data)

        if (data?.membership !== 'admin') {
          console.log('User is not admin')
          navigate('/')
          return
        }

        setUserRole(data.membership)
        fetchStats()
        fetchExpenses()
      } catch (error) {
        console.error('Error checking admin role:', error)
        navigate('/')
      }
    }

    checkAdminRole()
  }, [navigate])

  // ดึงข้อมูลรายจ่าย
  const fetchExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('expense_date', { ascending: false })

      if (error) throw error
      setExpenses(data || [])
    } catch (error) {
      console.error('Error fetching expenses:', error)
    }
  }

  // ฟังก์ชันสำหรับจัดกลุ่มข้อมูลตามช่วงเวลา
  const groupPaymentsByPeriod = (payments, type, currentDate) => {
    const periods = {}

    // สร้างช่วงวันที่ต้องการแสดง
    let startDate = new Date()
    let endDate = new Date()
    
    if (type === 'daily') {
      startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      endDate = new Date(currentDate)
    } else if (type === 'weekly') {
      startDate = new Date(currentDate)
      startDate.setDate(currentDate.getDate() - 7)
      endDate = new Date(currentDate)
    } else {
      // รายเดือนใช้โค้ดเดิม
      payments?.forEach(payment => {
        const date = new Date(payment.created_at)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        
        if (!periods[monthKey]) {
          periods[monthKey] = {
            date: monthKey,
            revenue: 0,
            transactions: new Set()
          }
        }
        periods[monthKey].revenue += payment.amount || 0
        periods[monthKey].transactions.add(payment.user_id)
      })

      return Object.keys(periods)
        .sort((a, b) => b.localeCompare(a))
        .map(key => ({
          date: periods[key].date,
          revenue: periods[key].revenue,
          transactions: periods[key].transactions.size
        }))
    }

    // สร้าง array ของทุกวันในช่วงที่ต้องการ
    const dates = []
    let current = new Date(startDate)
    while (current <= endDate) {
      dates.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }

    // จัดกลุ่มข้อมูลการชำระเงินที่อนุมัติแล้ว
    payments?.forEach(payment => {
      const paymentDate = new Date(payment.created_at)
      paymentDate.setHours(0, 0, 0, 0)
      const dateKey = paymentDate.toISOString().split('T')[0]
      
      if (!periods[dateKey]) {
        periods[dateKey] = {
          date: dateKey,
          revenue: 0,
          transactions: new Set()
        }
      }
      
      // เพิ่มข้อมูลเฉพาะรายการที่อนุมัติแล้ว
      if (payment.payment_status === 'approved') {
        periods[dateKey].revenue += payment.amount || 0
        periods[dateKey].transactions.add(payment.user_id)
      }
    })

    // สร้างข้อมูลสำหรับทุกวัน เรียงจากวันล่าสุดไปวันแรก
    return dates.map(date => {
      const dateKey = date.toISOString().split('T')[0]
      return {
        date: dateKey,
        revenue: periods[dateKey]?.revenue || 0,
        transactions: periods[dateKey]?.transactions?.size || 0
      }
    }).reverse() // เรียงจากวันล่าสุดไปวันแรก
  }

  // ฟังก์ชันคำนวณระยะเวลาทำการ
  const calculateOperatingTime = () => {
    const startDate = new Date('2024-12-03')
    const currentDate = new Date()
    
    const diffTime = Math.abs(currentDate - startDate)
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    const months = Math.floor(diffDays / 30)
    const days = diffDays % 30
    
    return `${months} เดือน ${days} วัน`
  }

  // ดึงข้อมูลสถิติ
  const fetchStats = async () => {
    try {
      setLoading(true)
      console.log('Fetching stats...')

      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)

      // คำนวณระยะเวลาทำการ
      const operatingTime = calculateOperatingTime()

      // ดึงข้อมูลสมาชิกทั้งหมด
      const { data: allMembers, error: membersError } = await supabase
        .from('profiles')
        .select('*')
        .neq('membership', 'admin')

      if (membersError) {
        console.error('Error fetching members:', membersError)
        throw membersError
      }

      // นับจำนวนสมาชิกแต่ละประเภท
      let freeCount = 0
      let vipCount = 0
      let lifetimeCount = 0
      let nearExpiryCount = 0
      let expiredCount = 0

      allMembers?.forEach(member => {
        if (member.membership === 'free') {
          freeCount++
        } else if (member.membership === 'vip') {
          vipCount++
        } else if (member.membership === 'lifetime') {
          lifetimeCount++
        }

        if (member.expires_at) {
          const expiryDate = new Date(member.expires_at)
          const daysLeft = Math.ceil((expiryDate - currentDate) / (1000 * 60 * 60 * 24))

          if (daysLeft <= 0) {
            expiredCount++
          } else if (daysLeft < 7) {
            nearExpiryCount++
          }
        }
      })

      // คำนวณวันที่เมื่อวาน
      const yesterdayDate = new Date(currentDate)
      yesterdayDate.setDate(currentDate.getDate() - 1)

      // ดึงข้อมูล payments ที่ได้รับการอนุมัติแล้ว
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .eq('payment_status', 'approved')
        .order('created_at', { ascending: false })

      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError)
        throw paymentsError
      }

      // ดึงข้อมูลรายจ่าย
      const { data: expenses, error: expensesError } = await supabase
        .from('expenses')
        .select('*')
        .order('created_at', { ascending: false })

      if (expensesError) {
        console.error('Error fetching expenses:', expensesError)
        throw expensesError
      }

      // คำนวณรายได้วันนี้
      const todayRevenue = payments
        ?.filter(payment => {
          const paymentDate = new Date(payment.created_at)
          return (
            paymentDate.getDate() === currentDate.getDate() &&
            paymentDate.getMonth() === currentDate.getMonth() &&
            paymentDate.getFullYear() === currentDate.getFullYear()
          )
        })
        .reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0

      // คำนวณรายได้เมื่อวาน
      const yesterdayRevenue = payments
        ?.filter(payment => {
          const paymentDate = new Date(payment.created_at)
          return (
            paymentDate.getDate() === yesterdayDate.getDate() &&
            paymentDate.getMonth() === yesterdayDate.getMonth() &&
            paymentDate.getFullYear() === yesterdayDate.getFullYear()
          )
        })
        .reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0

      // คำนวณรายได้เดือนนี้
      const monthlyRevenue = payments
        ?.filter(payment => {
          const paymentDate = new Date(payment.created_at)
          return (
            paymentDate.getMonth() === currentDate.getMonth() &&
            paymentDate.getFullYear() === currentDate.getFullYear()
          )
        })
        .reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0

      // คำนวณรายจ่ายเดือนนี้
      const monthlyExpenses = expenses
        ?.filter(expense => {
          const expenseDate = new Date(expense.expense_date)
          return (
            expenseDate.getMonth() === currentDate.getMonth() &&
            expenseDate.getFullYear() === currentDate.getFullYear()
          )
        })
        .reduce((sum, expense) => sum + (expense.amount || 0), 0) || 0

      // คำนวณกำไรสุทธิเดือนนี้
      const monthlyProfit = monthlyRevenue - monthlyExpenses

      // คำนวณรายได้ทั้งหมด
      const totalRevenue = payments?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0

      // คำนวณรายจ่ายทั้งหมด
      const totalExpenses = expenses?.reduce((sum, expense) => sum + (expense.amount || 0), 0) || 0

      // คำนวณกำไรสุทธิทั้งหมด
      const totalProfit = totalRevenue - totalExpenses

      // คำนวณจำนวนผู้ทำรายการวันนี้
      const todayTransactions = new Set(
        payments
          ?.filter(payment => {
            const paymentDate = new Date(payment.created_at)
            return paymentDate >= currentDate
          })
          .map(payment => payment.user_id)
      ).size

      // จัดกลุ่มข้อมูลตามช่วงเวลา
      const periodStats = groupPaymentsByPeriod(payments, periodType, currentDate)

      setStats(prevStats => ({
        ...prevStats,
        totalMembers: allMembers?.length || 0,
        freeMembers: freeCount,
        vipMembers: vipCount,
        lifetimeMembers: lifetimeCount,
        nearExpiryMembers: nearExpiryCount,
        expiredMembers: expiredCount,
        todayRevenue,
        yesterdayRevenue,
        todayTransactions,
        monthlyRevenue,
        monthlyExpenses,
        monthlyProfit,
        totalRevenue,
        totalExpenses,
        totalProfit,
        operatingTime,      // เพิ่มระยะเวลาทำการ
        periodStats
      }))

    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  // เพิ่มฟังก์ชันสำหรับบันทึกรายจ่าย
  const handleSubmitExpense = async (e) => {
    e.preventDefault()
    
    try {
      const { data, error } = await supabase
        .from('expenses')
        .insert([{
          expense_date: expenseForm.expense_date,
          amount: parseFloat(expenseForm.amount),
          description: expenseForm.description,
          created_by: user?.id
        }])

      if (error) throw error

      // รีเซ็ตฟอร์มและดึงข้อมูลใหม่
      setExpenseForm({
        expense_date: new Date().toISOString().split('T')[0],
        amount: '',
        description: ''
      })
      
      // ดึงข้อมูลสถิติและรายจ่ายใหม่
      fetchStats()
      fetchExpenses()
      
      alert('บันทึกรายจ่ายเรียบร้อยแล้ว')
    } catch (error) {
      console.error('Error adding expense:', error)
      alert('เกิดข้อผิดพลาดในการบันทึกรายจ่าย')
    }
  }

  // เรียกดึงข้อมูลใหม่เมื่อเปลี่ยนช่วงเวลา
  useEffect(() => {
    if (userRole === 'admin') {
      fetchStats()
    }
  }, [userRole, periodType])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Helmet>
        <title>รายงาน | AI Unlocked</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-gray-800 shadow-xl rounded-lg p-6 border border-gray-700">
            <h1 className="text-2xl font-semibold text-white mb-6">
              รายงานระบบ ประจำวันที่{' '}
              {new Date().toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h1>
            
            <div className="space-y-8">
              {/* สถิติรายได้ */}
              <div className="grid grid-rows-2 gap-4">
                {/* แถวที่ 1: รายได้วันนี้ รายได้เมื่อวาน ผู้ทำรายการวันนี้ */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-green-900 to-gray-800 p-4 rounded-lg border-2 border-green-500 shadow-lg shadow-green-900/50">
                    <h3 className="text-lg font-medium text-white mb-2">รายได้วันนี้</h3>
                    <p className="text-2xl font-bold text-green-400">{stats.todayRevenue.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-white mb-2">รายได้เมื่อวาน</h3>
                    <p className="text-2xl font-bold text-green-400">{stats.yesterdayRevenue.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-white mb-2">ผู้ทำรายการวันนี้</h3>
                    <p className="text-2xl font-bold text-purple-400">{stats.todayTransactions.toLocaleString()}</p>
                  </div>
                </div>
                
                {/* แถวที่ 2: รายได้เดือนนี้ รายจ่ายเดือนนี้ กำไรเดือนนี้ */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-emerald-900 to-gray-800 p-4 rounded-lg border-2 border-emerald-500 shadow-lg shadow-emerald-900/50">
                    <h3 className="text-lg font-medium text-white mb-2">รายได้เดือนนี้</h3>
                    <p className="text-2xl font-bold text-green-400">{stats.monthlyRevenue.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-white mb-2">รายจ่ายเดือนนี้</h3>
                    <p className="text-2xl font-bold text-red-400">{stats.monthlyExpenses.toLocaleString()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-900 to-gray-800 p-4 rounded-lg border-2 border-blue-500 shadow-lg shadow-blue-900/50">
                    <h3 className="text-lg font-medium text-white mb-2">กำไรเดือนนี้</h3>
                    <p className="text-2xl font-bold text-blue-400">{stats.monthlyProfit.toLocaleString()}</p>
                  </div>
                </div>

                {/* แถวที่ 3: รายได้ทั้งหมด รายจ่ายทั้งหมด กำไรสุทธิทั้งหมด */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-yellow-900 to-gray-800 p-4 rounded-lg border-2 border-yellow-500 shadow-lg shadow-yellow-900/50">
                    <h3 className="text-lg font-medium text-white mb-2">รายได้ทั้งหมด</h3>
                    <p className="text-2xl font-bold text-yellow-400">{stats.totalRevenue.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-white mb-2">รายจ่ายทั้งหมด</h3>
                    <p className="text-2xl font-bold text-red-400">{stats.totalExpenses.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-white mb-2">กำไรสุทธิทั้งหมด</h3>
                    <p className="text-2xl font-bold text-blue-400">{stats.totalProfit.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-white mb-2">เวลาทำการ</h3>
                    <p className="text-2xl font-bold text-orange-400">{stats.operatingTime}</p>
                  </div>
                </div>
              </div>

              {/* สถิติสมาชิก */}
              <div className="grid grid-cols-6 gap-4">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-white mb-2">Members</h3>
                  <p className="text-2xl font-bold text-red-400">{stats.totalMembers}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-white mb-2">VIP</h3>
                  <p className="text-2xl font-bold text-yellow-400">{stats.vipMembers}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-white mb-2">Lifetime</h3>
                  <p className="text-2xl font-bold text-purple-400">{stats.lifetimeMembers}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-white mb-2">Free</h3>
                  <p className="text-2xl font-bold text-blue-400">{stats.freeMembers}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-white mb-2">Near Exp</h3>
                  <p className="text-2xl font-bold text-orange-400">{stats.nearExpiryMembers}</p>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-white mb-2">Expired</h3>
                  <p className="text-2xl font-bold text-gray-400">{stats.expiredMembers}</p>
                </div>
              </div>

              {/* รายงานตามช่วงเวลา */}
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-white">รายงานตามช่วงเวลา</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPeriodType('daily')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        periodType === 'daily'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      รายวัน
                    </button>
                    <button
                      onClick={() => setPeriodType('weekly')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        periodType === 'weekly'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      7 วันล่าสุด
                    </button>
                    <button
                      onClick={() => setPeriodType('month')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        periodType === 'month'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      รายเดือน
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          วันที่
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          รายรับ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          ผู้ทำรายการ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {stats.periodStats.map((period, index) => (
                        <tr key={period.date} className={index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {period.date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                            {period.revenue.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {period.transactions} คน
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ฟอร์มบันทึกรายจ่าย */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">บันทึกรายจ่าย</h2>
                <form onSubmit={handleSubmitExpense} className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">วันที่</label>
                      <input
                        type="date"
                        value={expenseForm.expense_date}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, expense_date: e.target.value }))}
                        className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">จำนวนเงิน</label>
                      <input
                        type="number"
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                        className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">รายละเอียด</label>
                      <input
                        type="text"
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="ระบุรายละเอียดรายจ่าย"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-2 rounded-lg transition-colors duration-200"
                    >
                      บันทึกรายจ่าย
                    </button>
                  </div>
                </form>

                {/* ตารางแสดงรายการรายจ่าย */}
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-white mb-4">รายการรายจ่าย</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-gray-300">
                      <thead className="bg-gray-700">
                        <tr>
                          <th className="px-4 py-3">วันที่</th>
                          <th className="px-4 py-3">รายละเอียด</th>
                          <th className="px-4 py-3 text-right">จำนวนเงิน</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {expenses.map(expense => (
                          <tr key={expense.id} className="hover:bg-gray-700">
                            <td className="px-4 py-3">
                              {new Date(expense.expense_date).toLocaleDateString('th-TH', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </td>
                            <td className="px-4 py-3">{expense.description}</td>
                            <td className="px-4 py-3 text-right text-red-400">
                              {expense.amount.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        {expenses.length === 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-3 text-center text-gray-500">
                              ไม่พบรายการรายจ่าย
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-gray-700">
                        <tr>
                          <td colSpan={2} className="px-4 py-3 font-medium">รวมทั้งหมด</td>
                          <td className="px-4 py-3 text-right font-medium text-red-400">
                            {expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0).toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default Report
